(function (root) {
  'use strict';

  function resultSetKey(input) {
    const url = new URL(input, 'https://aghsanshop.com');
    url.hash = '';
    for (const key of [...url.searchParams.keys()]) {
      if (key === 'page' || key.startsWith('page_') || key === 'section_id') {
        url.searchParams.delete(key);
      }
    }
    url.searchParams.sort();
    return url.toString();
  }

  function productKey(input) {
    if (!input) return null;
    const url = new URL(input, 'https://aghsanshop.com');
    const match = url.pathname.match(/\/products\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function sectionRequestUrl(input, sectionId) {
    const url = new URL(input);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function shouldRestore(navigationType, saved, key, now) {
    return (navigationType === 'reload' || navigationType === 'back_forward') &&
      saved?.key === key && Number.isInteger(saved.pagesLoaded) && saved.pagesLoaded > 1 &&
      saved.pagesLoaded <= 100 && Number.isFinite(saved.scrollY) && saved.scrollY >= 0 &&
      Number.isFinite(saved.savedAt) && now >= saved.savedAt && now - saved.savedAt <= 30 * 60 * 1000;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { resultSetKey, productKey, sectionRequestUrl, shouldRestore };
  }

  if (!root?.document || !root.customElements || root.customElements.get('hdt-infinite-products')) return;

  const seenSections = new Set();

  class InfiniteProducts extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('section-id');
      this.gridId = this.getAttribute('append');
      this.section = this.closest('.shopify-section');
      this.grid = this.section?.querySelector('[id="' + CSS.escape(this.gridId) + '"]');
      this.link = this.querySelector('a[rel="next"]');
      this.retry = this.querySelector('[data-infinite-retry]');
      if (!this.sectionId || !this.grid || !this.link || !root.IntersectionObserver) return;

      this.nextUrl = this.link.href;
      this.key = resultSetKey(root.location.href);
      this.stateKey = new URL(root.location.href).toString().split('#')[0];
      this.storageKey = 'aghsan:infinite:' + this.stateKey;
      this.pagesLoaded = 1;
      this.loadedUrls = new Set([root.location.href]);
      this.seenProducts = new Set();
      this.grid.querySelectorAll('hdt-card-product a[data-pr-url]').forEach((link) => {
        const key = productKey(link.href);
        if (key) this.seenProducts.add(key);
      });
      this.loading = false;
      this.failed = false;
      this.stopped = false;
      this.paused = false;
      this.controller = new AbortController();
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      }, { rootMargin: '0px 0px 600px 0px' });

      this.retry?.addEventListener('click', () => {
        this.failed = false;
        this.retry.hidden = true;
        this.loadNext();
      }, { signal: this.controller.signal });
      root.document.addEventListener('facet:render', (event) => {
        if (event.detail?.url?.searchParams.get('section_id') === this.sectionId) this.pause();
      }, { signal: this.controller.signal });
      root.addEventListener('popstate', () => {
        if (resultSetKey(root.location.href) !== this.key) this.pause();
      }, { signal: this.controller.signal });
      root.addEventListener('pagehide', () => this.saveState(), { signal: this.controller.signal });
      this.grid.addEventListener('click', (event) => {
        if (event.target.closest('a[data-pr-url]')) this.saveState();
      }, { signal: this.controller.signal });
      let scrollTimer;
      root.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this.saveState(false), 350);
      }, { passive: true, signal: this.controller.signal });

      this.setAttribute('enhanced', '');
      const firstConnection = !seenSections.has(this.sectionId);
      seenSections.add(this.sectionId);
      const historyState = root.history.state?.aghsanInfinite;
      const navigationType = root.performance.getEntriesByType('navigation')[0]?.type;
      let saved = historyState?.key === this.stateKey ? historyState : null;
      if (!saved && firstConnection && (navigationType === 'reload' || navigationType === 'back_forward')) {
        try { saved = JSON.parse(root.sessionStorage.getItem(this.storageKey)); } catch (_) { /* Storage may be disabled. */ }
      }
      if (saved && ((historyState?.key === this.stateKey && shouldRestore('reload', saved, this.stateKey, Date.now())) ||
        shouldRestore(navigationType, saved, this.stateKey, Date.now()))) {
        this.restore(saved);
      } else {
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.request?.abort();
      this.controller?.abort();
    }

    pause() {
      this.paused = true;
      this.observer?.disconnect();
      this.request?.abort();
    }

    saveState(updateHistory = true) {
      if (!this.isConnected || this.paused) return;
      const saved = {
        key: this.stateKey,
        pagesLoaded: this.pagesLoaded,
        scrollY: root.scrollY,
        savedAt: Date.now(),
      };
      try { root.sessionStorage.setItem(this.storageKey, JSON.stringify(saved)); } catch (_) { /* Optional restoration. */ }
      if (updateHistory) {
        try { root.history.replaceState({ ...(root.history.state || {}), aghsanInfinite: saved }, '', root.location.href); } catch (_) { /* Keep navigation native. */ }
      }
    }

    async restore(saved) {
      for (let page = 1; page < saved.pagesLoaded && this.nextUrl && !this.paused; page += 1) {
        const ok = await this.loadNext();
        if (!ok) break;
      }
      root.requestAnimationFrame(() => root.requestAnimationFrame(() => root.scrollTo(0, saved.scrollY)));
      if (!this.stopped && !this.failed && !this.paused) this.observer.observe(this);
    }

    async loadNext() {
      if (this.loading || this.failed || this.stopped || this.paused || !this.nextUrl || !this.isConnected) return false;
      const next = new URL(this.nextUrl, root.location.href);
      if (next.origin !== root.location.origin || resultSetKey(next.href) !== resultSetKey(root.location.href) || this.loadedUrls.has(next.href)) {
        this.stop();
        return false;
      }
      this.loading = true;
      this.setAttribute('loading', '');
      this.request = new AbortController();
      try {
        const response = await root.fetch(sectionRequestUrl(next.href, this.sectionId), {
          signal: this.request.signal,
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error('Page request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (this.paused || !this.isConnected || resultSetKey(root.location.href) !== this.key) return false;
        const rendered = html.getElementById('shopify-section-' + this.sectionId);
        const fetchedGrid = rendered?.querySelector('[id="' + CSS.escape(this.gridId) + '"]');
        if (!fetchedGrid) throw new Error('Product grid missing from section response');
        const nextLink = rendered.querySelector('hdt-infinite-products a[rel="next"]');
        const fragment = root.document.createDocumentFragment();
        const newCards = [];
        for (const child of [...fetchedGrid.children]) {
          const key = productKey(child.querySelector('hdt-card-product a[data-pr-url]')?.href ||
            (child.matches('hdt-card-product') ? child.querySelector('a[data-pr-url]')?.href : null));
          if (key && this.seenProducts.has(key)) continue;
          if (key) this.seenProducts.add(key);
          const node = root.document.importNode(child, true);
          if (node.nodeType === 1 && node.matches('hdt-card-product')) {
            node.setAttribute('data-aghsan-infinite-new', String(this.pagesLoaded + 1));
            newCards.push(node);
          }
          fragment.appendChild(node);
        }
        this.grid.appendChild(fragment);
        if (newCards.length) {
          this.grid.dispatchEvent(new CustomEvent('products:update', { bubbles: true, detail: { added: newCards } }));
          if (this.grid.enabledReveal && typeof this.grid.reveal === 'function' &&
            !root.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.grid.appendEl = '[data-aghsan-infinite-new="' + (this.pagesLoaded + 1) + '"]';
            this.grid.reveal();
            this.grid.appendEl = '';
          }
        }
        this.loadedUrls.add(next.href);
        this.pagesLoaded += 1;
        this.nextUrl = nextLink?.href || null;
        if (this.nextUrl) this.link.href = this.nextUrl;
        else this.stop();
        this.saveState();
        return true;
      } catch (error) {
        if (!this.request.signal.aborted && !this.paused && this.isConnected) {
          this.failed = true;
          this.observer.disconnect();
          if (this.retry) this.retry.hidden = false;
        }
        return false;
      } finally {
        this.loading = false;
        this.removeAttribute('loading');
      }
    }

    stop() {
      this.stopped = true;
      this.observer?.disconnect();
      this.link?.remove();
    }
  }

  root.customElements.define('hdt-infinite-products', InfiniteProducts);
})(typeof window !== 'undefined' ? window : null);
